import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContactMergePreviewResponse } from "@multi-chat/shared";
import { Cake, GitMerge, Lock } from "lucide-react";
import { Button } from "../../../components/ui/button/index.js";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogTitle,
} from "../../../components/ui/alert-dialog/index.js";
import { executeContactMerge, previewContactMerge } from "../../../api/contacts-api.js";
import { LinkedPlatformBadges } from "../LinkedPlatformBadges.js";
import { ContactSearchPicker } from "../ContactSearchPicker.js";
import { mapContactError } from "../contact-errors.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { formatContactBirthday } from "../ContactBirthdayField.js";
import {
  contactsModalBodyClass,
  contactsModalFooterClass,
  contactsModalHeaderClass,
} from "../contacts-dialog-presentation.js";
import { ContactsModalContent } from "../ContactsModalContent.js";

export function MergeContactsDialog({
  open,
  organizationId,
  primaryContactId,
  onClose,
  onMerged,
}: {
  open: boolean;
  organizationId: number;
  primaryContactId: number;
  onClose: () => void;
  onMerged: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith("he") ? "he-IL" : "en-US";
  const [secondaryContactId, setSecondaryContactId] = useState<number | null>(null);
  const [preview, setPreview] = useState<ContactMergePreviewResponse | null>(null);
  const [survivingPhoneContactId, setSurvivingPhoneContactId] = useState<number | null>(null);
  const [survivingEmailContactId, setSurvivingEmailContactId] = useState<number | null>(null);
  const [survivingBirthdayContactId, setSurvivingBirthdayContactId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setSecondaryContactId(null);
      setPreview(null);
      setSurvivingPhoneContactId(null);
      setSurvivingEmailContactId(null);
      setSurvivingBirthdayContactId(null);
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open || secondaryContactId == null) {
      setPreview(null);
      return;
    }
    setLoading(true);
    setError(null);
    void previewContactMerge(organizationId, {
      primaryContactId,
      secondaryContactId,
      survivingPhoneContactId: survivingPhoneContactId ?? undefined,
      survivingEmailContactId: survivingEmailContactId ?? undefined,
      survivingBirthdayContactId: survivingBirthdayContactId ?? undefined,
    })
      .then(setPreview)
      .catch((err) => {
        setPreview(null);
        setError(mapContactError(t, err, t("contacts.merge.previewError")));
      })
      .finally(() => setLoading(false));
  }, [
    open,
    organizationId,
    primaryContactId,
    secondaryContactId,
    survivingPhoneContactId,
    survivingEmailContactId,
    survivingBirthdayContactId,
    t,
  ]);

  const birthdayChoiceMissing = Boolean(preview?.birthdayConflict) && survivingBirthdayContactId == null;

  async function handleMerge(): Promise<void> {
    if (secondaryContactId == null || !preview?.canMerge || birthdayChoiceMissing) return;
    setLoading(true);
    setError(null);
    try {
      await executeContactMerge(organizationId, {
        primaryContactId,
        secondaryContactId,
        survivingPhoneContactId: survivingPhoneContactId ?? undefined,
        survivingEmailContactId: survivingEmailContactId ?? undefined,
        survivingBirthdayContactId: survivingBirthdayContactId ?? undefined,
      });
      onMerged();
      onClose();
    } catch (err) {
      setError(mapContactError(t, err, t("contacts.merge.error")));
    } finally {
      setLoading(false);
    }
  }

  const blockingMessage = preview?.blockingCode
    ? preview.blockingCode === "SELF_MERGE"
      ? t("contacts.merge.conflicts.self")
      : t("contacts.merge.conflicts.platform", {
          platform: t(`app.nav.${(preview.blockingPlatformType ?? "whatsapp").toLowerCase()}`),
        })
    : preview && !preview.canMerge
      ? t("contacts.errors.mergeConflict")
      : null;

  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onClose()}>
      <ContactsModalContent className="w-[min(92vw,32rem)]">
        <div className={contactsModalHeaderClass}>
          <AlertDialogTitle>{t("contacts.merge.title")}</AlertDialogTitle>
          <AlertDialogDescription className="m-0 text-[0.8125rem] text-muted">
            {t("contacts.merge.description")}
          </AlertDialogDescription>
        </div>

        <div className={contactsModalBodyClass}>
          <ContactSearchPicker
            organizationId={organizationId}
            excludeContactId={primaryContactId}
            selectedContactId={secondaryContactId}
            onSelect={(contact) => setSecondaryContactId(Number(contact.id))}
            disabled={loading}
            searchLabel={t("contacts.merge.searchLabel")}
            searchPlaceholder={t("contacts.merge.searchPlaceholder")}
          />

          {preview ? (
            <div className="space-y-3 rounded-md border border-border/70 bg-surface-muted/40 p-3 text-[0.8125rem]">
              {!preview.canMerge && blockingMessage ? (
                <p className="m-0 text-red-600">{blockingMessage}</p>
              ) : null}
              {preview.canMerge ? (
                <>
                  <p className="m-0 font-medium">{preview.resultingDisplayName}</p>
                  {preview.resultingPhone ? (
                    <p className="m-0 text-muted">
                      <LtrValue title={preview.resultingPhone}>{preview.resultingPhone}</LtrValue>
                    </p>
                  ) : null}
                  {preview.resultingEmail ? (
                    <p className="m-0 text-muted">
                      <LtrValue title={preview.resultingEmail}>{preview.resultingEmail}</LtrValue>
                    </p>
                  ) : null}
                  {preview.resultingBirthday ? (
                    <p className="m-0 flex items-center gap-1.5 text-muted" data-testid="merge-resulting-birthday">
                      <Cake size={13} aria-hidden="true" className="shrink-0" />
                      {formatContactBirthday(preview.resultingBirthday, locale)}
                    </p>
                  ) : null}
                  <LinkedPlatformBadges types={preview.resultingPlatformTypes} />
                  {preview.phoneConflict && preview.whatsappPhoneAuthoritative ? (
                    <div className="space-y-1 rounded-md border border-border/60 bg-surface-muted/30 p-2.5">
                      <p className="m-0 flex items-center gap-1.5 text-[0.75rem] font-medium">
                        <Lock size={13} aria-hidden="true" className="shrink-0 text-muted" />
                        {t("contacts.merge.whatsappPhoneLockedTitle")}
                      </p>
                      {preview.resultingPhone ? (
                        <>
                          <p className="m-0 text-[0.75rem] text-muted">
                            {t("contacts.merge.whatsappPhoneLockedHelp", {
                              phone: preview.resultingPhone,
                            })}
                          </p>
                          <p className="m-0 text-[0.8125rem] font-medium">
                            <LtrValue title={preview.resultingPhone}>
                              {preview.resultingPhone}
                            </LtrValue>
                          </p>
                        </>
                      ) : null}
                    </div>
                  ) : preview.phoneConflict ? (
                    <fieldset className="space-y-1">
                      <legend className="text-[0.75rem] font-medium">
                        {t("contacts.merge.phoneConflict")}
                      </legend>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="surviving-phone"
                          checked={(survivingPhoneContactId ?? primaryContactId) === primaryContactId}
                          onChange={() => setSurvivingPhoneContactId(primaryContactId)}
                        />
                        {t("contacts.merge.keepPrimaryPhone")}
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="surviving-phone"
                          checked={survivingPhoneContactId === secondaryContactId}
                          onChange={() => setSurvivingPhoneContactId(secondaryContactId!)}
                        />
                        {t("contacts.merge.keepSecondaryPhone")}
                      </label>
                    </fieldset>
                  ) : null}
                  {preview.emailConflict ? (
                    <fieldset className="space-y-1">
                      <legend className="text-[0.75rem] font-medium">
                        {t("contacts.merge.emailConflict")}
                      </legend>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="surviving-email"
                          checked={(survivingEmailContactId ?? primaryContactId) === primaryContactId}
                          onChange={() => setSurvivingEmailContactId(primaryContactId)}
                        />
                        {t("contacts.merge.keepPrimaryEmail")}
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="surviving-email"
                          checked={survivingEmailContactId === secondaryContactId}
                          onChange={() => setSurvivingEmailContactId(secondaryContactId!)}
                        />
                        {t("contacts.merge.keepSecondaryEmail")}
                      </label>
                    </fieldset>
                  ) : null}
                  {preview.birthdayConflict ? (
                    <fieldset className="space-y-1" data-testid="merge-birthday-conflict">
                      <legend className="flex items-center gap-1.5 text-[0.75rem] font-medium">
                        <Cake size={13} aria-hidden="true" className="shrink-0 text-muted" />
                        {t("contacts.merge.birthdayConflict")}
                      </legend>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="surviving-birthday"
                          checked={survivingBirthdayContactId === primaryContactId}
                          onChange={() => setSurvivingBirthdayContactId(primaryContactId)}
                        />
                        {t("contacts.merge.keepPrimaryBirthday", {
                          date: formatContactBirthday(preview.primaryBirthday, locale),
                        })}
                      </label>
                      <label className="flex items-center gap-2">
                        <input
                          type="radio"
                          name="surviving-birthday"
                          checked={survivingBirthdayContactId === secondaryContactId}
                          onChange={() => setSurvivingBirthdayContactId(secondaryContactId!)}
                        />
                        {t("contacts.merge.keepSecondaryBirthday", {
                          date: formatContactBirthday(preview.secondaryBirthday, locale),
                        })}
                      </label>
                      {birthdayChoiceMissing ? (
                        <p className="m-0 text-[0.75rem] text-muted">
                          {t("contacts.merge.birthdayChoiceRequired")}
                        </p>
                      ) : null}
                    </fieldset>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}

          {error ? <p className="text-[0.8125rem] text-red-600">{error}</p> : null}
        </div>

        <div className={contactsModalFooterClass}>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t("common.cancel")}
          </Button>
          <Button
            disabled={loading || !preview?.canMerge || birthdayChoiceMissing}
            onClick={() => void handleMerge()}
            className="gap-1.5"
          >
            <GitMerge size={14} aria-hidden="true" />
            {loading ? t("contacts.merge.merging") : t("contacts.merge.confirm")}
          </Button>
        </div>
      </ContactsModalContent>
    </AlertDialog>
  );
}
